import {
	ButtonDropdown,
	DropdownList,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	IconTooltip,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../shared/components/modals/types';
import Util from '../../../shared/util/util';
import type {StageProfile} from '../../../shared/util/projectStageUtil';
import CreateProjectStageMutation from '../../../../mutations/CreateProjectStageMutation';
import UpdateProjectStageMutation from '../../../../mutations/UpdateProjectStageMutation';
import {
	isStageLastWithProfile,
	MAX_STAGE_NAME_LENGTH,
	StageNameError,
	stageNameErrorFromServer,
	validateStageName,
} from '../projectStageValidation';

export type ProjectStageModalPayload = {
	/** The company's Relay id; a new stage is appended to its projectStages connection. */
	companyId: string;
	/** The stage being edited. Absent when adding a stage. */
	stage?: {id: string; name: string; profile: StageProfile};
	/** Every other stage's stored name, defaults included, for the duplicate check. */
	otherNames: ReadonlyArray<string>;
	/** Whether no other stage has the edited stage's profile, as the page saw it. */
	lastWithProfile?: boolean;
};

export const showProjectStageModal = (props: ProjectStageModalPayload): void => {
	showModal({
		type: MODAL_TYPE.PROJECT_STAGE,
		...props,
	});
};

const ERROR_MESSAGE_IDS: Record<StageNameError, string> = {
	required: 'settings.project_stages.error.name_required',
	duplicate: 'settings.project_stages.error.name_duplicate',
};

const PROFILES: StageProfile[] = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

/** Adds a project stage, or edits one's name and what it behaves like. Saving happens here, not on the page. */
export const ProjectStageModal = ({
	closeModal,
	companyId,
	stage,
	otherNames,
	lastWithProfile: lastWithProfileOnOpen = false,
}: ModalProps<ProjectStageModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [name, setName] = useState(stage?.name ?? '');
	const [profile, setProfile] = useState<StageProfile>(stage?.profile ?? 'PLANNING');
	const [touched, setTouched] = useState(false);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState<StageNameError | null>(null);
	const [lastWithProfile, setLastWithProfile] = useState(lastWithProfileOnOpen);
	const [warning, setWarning] = useState(false);

	// A save outlives the modal if it is cancelled or dismissed mid-flight. The conductor's closeModal
	// pops whichever modal is on top, so a late success must not call it for a modal that is already gone.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const profileLabel = (value: StageProfile) => intl.formatMessage({id: `project_status.${value.toLowerCase()}`});
	// Opportunity is offered with opportunity access, or when it is already this stage's profile.
	const profileOptions = PROFILES.filter(
		value => value !== 'OPPORTUNITY' || Util.hasOpportunityAccess() || stage?.profile === 'OPPORTUNITY'
	).map(value => ({id: value, name: profileLabel(value)}));

	const remapping = !!stage && profile !== stage.profile;
	const localError = validateStageName(name, otherNames);
	// "Required" is only worth saying once the admin has typed and cleared the field; an untouched empty
	// field just keeps Save disabled.
	const shownError = serverError ?? (localError === 'required' && !touched ? null : localError);

	const handleChange = (value: string) => {
		setName(value);
		setTouched(true);
		setServerError(null);
	};

	const handleProfileChange = (value: StageProfile) => {
		setProfile(value);
		setWarning(false);
	};

	// Saving only ends on a path that really ends it: the default handler returns true when it has
	// scheduled a retry, and Save must stay disabled until that attempt settles.
	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		if (!mounted.current) {
			onFailure(error);
			return;
		}
		const nameError = stageNameErrorFromServer(error);
		if (nameError) {
			setServerError(nameError);
			setSaving(false);
			return;
		}
		if (isStageLastWithProfile(error)) {
			// Another stage with the old profile went away since the page loaded: warn, then let the admin go ahead.
			setLastWithProfile(true);
			setWarning(true);
			setSaving(false);
			return;
		}
		if (onFailure(error) !== true) {
			setSaving(false);
		}
	};

	const handleSaved = () => {
		if (mounted.current) {
			closeModal();
		}
	};

	const commit = (allowLastWithProfile: boolean) => {
		setSaving(true);
		const trimmed = name.trim();
		if (stage) {
			Util.CommitMutation(
				UpdateProjectStageMutation,
				{id: stage.id, name: trimmed, profile, allowLastWithProfile},
				handleSaved,
				true,
				null,
				handleError
			);
		} else {
			// Creating is not idempotent: if the first request committed but its response was lost, an
			// automatic retry would be refused as a duplicate.
			Util.CommitMutation(
				CreateProjectStageMutation,
				{companyId, name: trimmed, profile},
				handleSaved,
				false,
				null,
				handleError
			);
		}
	};

	const handleSave = () => {
		if (localError || saving) {
			return;
		}
		if (remapping && lastWithProfile && !warning) {
			setWarning(true);
			return;
		}
		commit(warning);
	};

	const title = intl.formatMessage({id: stage ? 'settings.project_stages.edit_title' : 'settings.project_stages.add_title'});
	const saveLabel = warning
		? intl.formatMessage({id: 'settings.project_stages.change_anyway'})
		: intl.formatMessage({id: stage ? 'settings.project_stages.save_stage' : 'settings.project_stages.add'});

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<TextField
						label={intl.formatMessage({id: 'settings.project_stages.name_label'})}
						value={name}
						onValueChange={handleChange}
						maxLength={MAX_STAGE_NAME_LENGTH}
						errorText={shownError ? intl.formatMessage({id: ERROR_MESSAGE_IDS[shownError]}) : ''}
						data-cy="project-stage-name-field"
					/>
					<FlexColumn gap={'xs'}>
						<FlexRow gap={'xs'} alignItems={'center'}>
							<Label>{intl.formatMessage({id: 'settings.project_stages.behaves_like'})}</Label>
							<IconTooltip icon={'info'}>
								<IconTooltip.Item
									title={intl.formatMessage({id: 'settings.project_stages.behaves_like'})}
									details={intl.formatMessage({id: 'settings.project_stages.behaves_like.tooltip'})}
								/>
							</IconTooltip>
						</FlexRow>
						<div>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown
										emphasis={EMPHASIS.LOW}
										aria-label={`${intl.formatMessage({
											id: 'settings.project_stages.behaves_like',
										})} ${profileLabel(profile)}`}
										data-cy="project-stage-profile"
									>
										{profileLabel(profile)}
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={4} align={'start'}>
									{profileOptions.map(option => (
										<DropdownList.Item
											key={option.id}
											onClick={() => handleProfileChange(option.id)}
											data-cy={`project-stage-profile-${option.id}`}
										>
											{option.name}
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</div>
						<Text data-cy="project-stage-profile-description">
							{intl.formatMessage({id: `settings.project_stages.profile_description.${profile.toLowerCase()}`})}
						</Text>
					</FlexColumn>
					{warning && stage ? (
						<div role="alert" data-cy="project-stage-remap-warning">
							<Text>
								{intl.formatMessage(
									{id: 'settings.project_stages.remap_last_with_profile'},
									{name: stage.name, profile: profileLabel(stage.profile)}
								)}
							</Text>
						</div>
					) : null}
				</FlexColumn>
				<FlexRow>
					<Modal.PrimaryFooterButton
						onPress={handleSave}
						disabled={!!localError || saving}
						dataCy="project-stage-save"
					>
						{saveLabel}
					</Modal.PrimaryFooterButton>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						{intl.formatMessage({id: 'common.cancel'})}
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
